import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Clock, Sparkles, X } from "lucide-react";

/**
 * Global trial/upgrade banner shown in the dashboard chrome.
 * - Trialing: shows days remaining + "Upgrade now"
 * - Free (no plan): shows "Start 14-day trial"
 * - Past due: prompts to update payment
 * - Active/canceled/etc: nothing
 */
export default function TrialBanner() {
  const [b, setB] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("ruleai_trial_banner_dismissed") === "1"
  );

  useEffect(() => {
    api.get("/billing/status").then((r) => setB(r.data)).catch(() => {});
  }, []);

  if (!b || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem("ruleai_trial_banner_dismissed", "1");
    setDismissed(true);
  };

  let bg = "bg-primary text-primary-foreground";
  let icon = <Sparkles className="w-4 h-4" />;
  let text = null;
  let cta = null;

  if (b.subscription_status === "trialing") {
    const d = b.trial_days_remaining;
    const urgent = d !== null && d <= 3;
    bg = urgent ? "bg-orange-600 text-white" : "bg-primary text-primary-foreground";
    icon = <Clock className="w-4 h-4" />;
    text = <>Trial ends in <b>{d} day{d === 1 ? "" : "s"}</b> — add a card to keep everything running.</>;
    cta = <Link to="/app/billing" data-testid="trial-banner-cta" className="underline hover:no-underline">Manage plan</Link>;
  } else if (!b.plan) {
    text = <>You&rsquo;re a <b>founding member</b> — full access, free during beta. 50% off for life when we launch.</>;
    cta = <Link to="/app/billing" data-testid="upgrade-banner-cta" className="underline hover:no-underline">See what&rsquo;s coming</Link>;
  } else if (b.subscription_status === "past_due" || b.subscription_status === "unpaid") {
    bg = "bg-destructive text-destructive-foreground";
    text = <>Payment failed — please update your card to keep RuleAI active.</>;
    cta = <Link to="/app/billing" data-testid="past-due-banner-cta" className="underline hover:no-underline">Fix billing</Link>;
  } else {
    return null;
  }

  return (
    <div className={`${bg} px-8 py-2 flex items-center justify-between gap-4 text-sm`} data-testid="trial-banner">
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <span className="truncate">{text}</span>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        {cta}
        <button onClick={dismiss} data-testid="trial-banner-dismiss" aria-label="Dismiss">
          <X className="w-4 h-4 opacity-70 hover:opacity-100" />
        </button>
      </div>
    </div>
  );
}
